import { ButtonHTMLAttributes, forwardRef, useEffect, useRef } from 'react'
import { TransitionStatus } from 'react-transition-group'
import gsap from 'gsap'
import mergeRefs from 'merge-refs'

import Typography from '@/components/base/Typography'
import { useStore } from '@/contexts/store'

import { cn } from '@/lib/utils'

export type AudioTriggerProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  state: TransitionStatus
}

const AudioTrigger = forwardRef<HTMLButtonElement, AudioTriggerProps>(
  ({ state, ...props }, ref) => {
    const { strings } = useStore()
    const root = useRef<HTMLButtonElement>(null!)
    const refs = mergeRefs(root, ref)

    useEffect(() => {
      const quickToX = gsap.quickTo(root.current, 'x', {
        duration: 0.6,
        ease: 'power3.out'
      })
      const quickToY = gsap.quickTo(root.current, 'y', {
        duration: 0.6,
        ease: 'power3.out'
      })

      const handlePointerMove = (event: MouseEvent) => {
        const x = event.clientX - window.innerWidth * 0.5
        const y = event.clientY - window.innerHeight * 0.5
        quickToX(x)
        quickToY(y)
      }

      window.addEventListener('pointermove', handlePointerMove)

      return () => {
        window.removeEventListener('pointermove', handlePointerMove)
      }
    }, [])

    return (
      <button
        ref={refs}
        className={cn(
          'z-audio pointer-events-none fixed left-1/2 top-1/2',
          'transition-opacity duration-300 ease-out-cubic',
          {
            'opacity-0 duration-0': state === 'entering',
            'opacity-100': state === 'entered',
            'opacity-0': state === 'exiting'
          }
        )}
        {...props}
      >
        <span
          className={cn(
            'absolute left-0 top-0 flex -translate-x-1/2 -translate-y-1/2 px-[2rem] py-[1.4rem]',
            'rounded-full bg-white-light/10 backdrop-blur-sm'
          )}
        >
          <Typography variant="caption" className="whitespace-nowrap uppercase">
            {strings?.sound}
          </Typography>
        </span>
      </button>
    )
  }
)

export default AudioTrigger
